import { UsersThree, Info } from "@phosphor-icons/react";
import DatePicker from "../../../ui/DatePicker";
import CouplePartnersFields, {
  CoupleToggle,
} from "../../CouplePartnersFields";
import EnglishCommunicationToggle from "../../EnglishCommunicationToggle";
import GenderSelect from "../GenderSelect";
import ParentsFields from "./ParentsFields";

/**
 * Step 1 — פרטים בסיסיים. The only gated step: name + 10-digit phone
 * (or both partners in couple mode) are required to advance or save.
 */
function BasicStep({ form }) {
  const {
    formData,
    errors,
    handleChange,
    handlePartnerChange,
    handleCoupleToggle,
    formatPhone,
    clinicManagesCouples,
    englishCommunicationEnabled,
    isSpeechTherapist,
    canAssignToOtherTherapist,
    therapistRequired,
    activeTherapists,
    selectedTherapistId,
    setSelectedTherapistId,
    therapistId,
  } = form;

  return (
    <div className="pat-wiz-step-panel" key="step-basic">
      {canAssignToOtherTherapist &&
        (activeTherapists.length > 1 || therapistRequired) && (
        <div className="form-group assign-therapist-group">
          <label className={therapistRequired ? "required" : ""}>
            <UsersThree size={16} weight="bold" />
            שיוך למטפל/ת
          </label>
          <select
            value={selectedTherapistId || ""}
            onChange={(e) => setSelectedTherapistId(e.target.value)}
            className={`therapist-select${errors.therapistId ? " error" : ""}`}
          >
            {therapistRequired && <option value="">בחר/י מטפל/ת</option>}
            {activeTherapists.map((t) => (
              <option key={t.uid} value={t.uid}>
                {t.firstName} {t.lastName}
                {t.uid === therapistId ? " (אני)" : ""}
              </option>
            ))}
          </select>
          {errors.therapistId && (
            <span className="error-text">{errors.therapistId}</span>
          )}
          {selectedTherapistId && selectedTherapistId !== therapistId && (
            <span className="field-hint assign-hint">
              <Info size={14} weight="bold" />
              המטופל/ת ישויך/תשויך למטפל/ת שנבחר/ה ויופיע/תופיע ברשימה שלו/ה
            </span>
          )}
        </div>
      )}

      {(clinicManagesCouples || englishCommunicationEnabled) && (
        <div className="ptg-grid">
          {clinicManagesCouples && (
            <CoupleToggle
              checked={formData.isCouple}
              onChange={handleCoupleToggle}
            />
          )}

          {englishCommunicationEnabled && (
            <EnglishCommunicationToggle
              language={formData.communicationLanguage}
              onChange={(value) => handleChange("communicationLanguage", value)}
            />
          )}
        </div>
      )}

      {formData.isCouple ? (
        <CouplePartnersFields
          partner1={formData.partner1}
          partner2={formData.partner2}
          smsPrimaryPartner={formData.smsPrimaryPartner}
          errors={errors}
          onPartnerChange={handlePartnerChange}
          onPrimaryChange={(val) => handleChange("smsPrimaryPartner", val)}
        />
      ) : (
        <>
          <div className="form-row">
            <div className="form-group">
              <label className="required">שם פרטי</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                className={errors.firstName ? "error" : ""}
                placeholder="הכנס שם פרטי"
              />
              {errors.firstName && (
                <span className="error-text">{errors.firstName}</span>
              )}
            </div>
            <div className="form-group">
              <label className="required">שם משפחה</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                className={errors.lastName ? "error" : ""}
                placeholder="הכנס שם משפחה"
              />
              {errors.lastName && (
                <span className="error-text">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="required">מספר טלפון</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", formatPhone(e.target.value))}
              className={errors.phone ? "error" : ""}
              placeholder="0501234567"
              dir="ltr"
            />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
            <div className="field-explanation">
              <Info size={14} weight="bold" className="field-explanation-icon" />
              <span>מספר הטלפון ישמש לשליחת הודעות SMS אוטומטיות למטופל: אישורי הגעה לפגישות, תזכורות לטיפולים, וקישורים למילוי טפסים ושאלונים דיגיטליים. חשוב לוודא שזהו מספר נייד תקין ופעיל.</span>
            </div>
          </div>
        </>
      )}

      <div className="form-row">
        <div className="form-group">
          <label>תאריך לידה</label>
          <DatePicker
            value={formData.birthDate}
            onChange={(e) => handleChange("birthDate", e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>מגדר</label>
          <GenderSelect
            value={formData.gender}
            onChange={(value) => handleChange("gender", value)}
          />
        </div>
      </div>

      {/* For speech therapist clinics: parents live in the first step */}
      {isSpeechTherapist && <ParentsFields form={form} />}

      <div className="form-group">
        <label>סיבת פנייה</label>
        <textarea
          value={formData.referralReason}
          onChange={(e) => handleChange("referralReason", e.target.value)}
          className={errors.referralReason ? "error" : ""}
          placeholder="תאר את הסיבה לפנייה..."
          rows={3}
        />
        {errors.referralReason && (
          <span className="error-text">{errors.referralReason}</span>
        )}
      </div>

      {/* For speech therapist clinics: family notes stay in the basic step,
          after the referral reason */}
      {isSpeechTherapist && (
        <div className="form-group">
          <label>הערות משפחתיות</label>
          <textarea
            value={formData.familyNotes}
            onChange={(e) => handleChange("familyNotes", e.target.value)}
            placeholder="למשל: הורים גרושים, לשלוח חשבונית לשניהם..."
            rows={3}
          />
        </div>
      )}
    </div>
  );
}

export default BasicStep;
